import { DataTable } from "@/components/Base/Datatable";
import { useEffect, useState, useState as useReactState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { usePaginatedCustomerUpdates } from "@/hooks/queries/usePaginatedCustomerUpdates";
import {
  DatatableCustomerUpdate,
  customerUpdateColumns,
  layoutDefaults,
} from "@/components/CustomerUpdate/CustomerUpdates.columns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { Button } from "@/components/Base/Button";
import {
  IconNames,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  Container,
  LOCAL_STORAGE_BASE_KEYS,
} from "@/types/v2";
import { Icon } from "@/components/Common/Icon";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { Input } from "@/components/Base/Input";
import { useAuth } from "@/hooks/store/useAuth";
import { fetchAllCustomerUpdates } from "@/lib/customerUpdates";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { getEmployeeName } from "@/lib/employees";
import { getContainerName } from "@/lib/getContainerName";
import { Select } from "@/components/Common/Select";
import { CheckboxInput } from "@/components/Base/CheckboxInput";
import { useUserColumns } from "@/hooks/useUserColumns";
import { useTableState } from "@/hooks/useTableState";

interface CustomerUpdatesTableProps {
  shopId: string;
  containers: Container[];
}

// Type for the pre-formatted export data
type CustomerUpdateExportData = Omit<
  DatatableCustomerUpdate,
  | "desiredUpdateTime"
  | "scheduledUpdateTime"
  | "actualUpdateTime"
  | "createdAt"
  | "updatedAt"
  | "scheduledAt"
  | "pendingAt"
  | "completedAt"
  | "skippedAt"
  | "cancelledAt"
  | "userId"
  | "createdBy"
  | "updatedBy"
  | "scheduledBy"
  | "pendingBy"
  | "completedBy"
  | "skippedBy"
  | "cancelledBy"
> & {
  desiredUpdateTime?: string;
  scheduledUpdateTime?: string;
  actualUpdateTime?: string;
  createdAt?: string;
  updatedAt?: string;
  scheduledAt?: string;
  pendingAt?: string;
  completedAt?: string;
  skippedAt?: string;
  cancelledAt?: string;
  userId: string; // Employee name instead of ID
  createdBy: string; // Employee name instead of ID
  updatedBy: string; // Employee name instead of ID
  scheduledBy: string; // Employee name instead of ID
  pendingBy: string; // Employee name instead of ID
  completedBy: string; // Employee name instead of ID
  skippedBy: string; // Employee name instead of ID
  cancelledBy: string; // Employee name instead of ID
};

// Helper function to get default sorting based on status
const getDefaultSortingForStatus = (
  status: CustomerUpdateStatus | "all" | undefined
): SortingState => {
  switch (status) {
    case CustomerUpdateStatus.PENDING:
      return [{ id: "scheduledUpdateTime", desc: false }];
    case CustomerUpdateStatus.CREATED:
      return [{ id: "createdAt", desc: true }];
    case CustomerUpdateStatus.SCHEDULED:
      return [{ id: "scheduledUpdateTime", desc: false }];
    case CustomerUpdateStatus.SNOOZED:
      return [{ id: "scheduledUpdateTime", desc: false }];
    case CustomerUpdateStatus.COMPLETED:
      return [{ id: "completedAt", desc: true }];
    case CustomerUpdateStatus.CANCELLED:
      return [{ id: "cancelledAt", desc: true }];
    case CustomerUpdateStatus.SKIPPED:
      return [{ id: "skippedAt", desc: true }];
    case "all":
      return [{ id: "createdAt", desc: true }];
    default:
      return [{ id: "createdAt", desc: true }];
  }
};

export const CustomerUpdatesTable = ({
  shopId,
  containers,
}: CustomerUpdatesTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceAdvisors = useServiceAdvisors();
  const { authUser } = useAuth();

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    LOCAL_STORAGE_BASE_KEYS.CUSTOMER_UPDATE_TABLE_COLUMNS,
    layoutDefaults
  );

  const { savedState, saveTableState, resetTableState } = useTableState(
    LOCAL_STORAGE_BASE_KEYS.CUSTOMER_UPDATE_TABLE_STATE,
    {
      sorting: [{ id: "createdAt", desc: true }],
      filtering: { status: CustomerUpdateStatus.PENDING },
      pagination: { pageIndex: 0, pageSize: 10 },
    }
  );

  // Get values from URL params, falling back to saved state, then defaults
  const currentPage = parseInt(
    searchParams.get("page") ||
      `${(savedState.pagination?.pageIndex ?? 0) + 1}`,
    10
  );
  const pageSize = parseInt(
    searchParams.get("pageSize") ||
      `${savedState.pagination?.pageSize ?? defaultState.pagination?.pageSize ?? 10}`,
    10
  );
  const sortBy =
    searchParams.get("sortBy") ?? savedState.sorting?.[0]?.id ?? "createdAt";
  const sortOrder =
    (searchParams.get("sortOrder") as "asc" | "desc") ??
    (savedState.sorting?.[0]?.desc ? "desc" : "asc");
  const status =
    searchParams.get("status") ||
    savedState.filtering?.status ||
    CustomerUpdateStatus.PENDING;
  const userId =
    searchParams.get("userId") || savedState.filtering?.userId || undefined;
  const cardNumberFilter =
    searchParams.get("cardNumber") ||
    savedState.filtering?.cardNumber ||
    undefined;

  const { data, isLoading } = usePaginatedCustomerUpdates({
    shopId,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    status: status as CustomerUpdateStatus | undefined,
    userId,
    cardNumberFilter,
  });

  // Export function to fetch all filtered data
  const handleExportData = async (): Promise<CustomerUpdateExportData[]> => {
    const allCustomerUpdates = await fetchAllCustomerUpdates(
      shopId,
      {
        userId,
        status: status as CustomerUpdateStatus | "all",
        cardNumberFilter,
      },
      {
        sortBy,
        sortOrder,
      }
    );

    // Transform the data the same way as displayed data
    return allCustomerUpdates.map((cu) => ({
      ...cu,
      comments:
        cu.status === CustomerUpdateStatus.CANCELLED
          ? cu.cancelledReason
          : cu.status === CustomerUpdateStatus.SKIPPED
            ? cu.skippedReason
            : cu.comments,
      desiredUpdateTime: firestoreToDateTime(
        cu.desiredUpdateTime
      )?.toLocaleString(),
      scheduledUpdateTime: firestoreToDateTime(
        cu.scheduledUpdateTime
      )?.toLocaleString(),
      actualUpdateTime: firestoreToDateTime(
        cu.actualUpdateTime
      )?.toLocaleString(),
      createdAt: firestoreToDateTime(cu.createdAt)?.toLocaleString(),
      updatedAt: firestoreToDateTime(cu.updatedAt)?.toLocaleString(),
      scheduledAt: cu.scheduledAt
        ? firestoreToDateTime(cu.scheduledAt)?.toLocaleString()
        : undefined,
      pendingAt: cu.pendingAt
        ? firestoreToDateTime(cu.pendingAt)?.toLocaleString()
        : undefined,
      skippedAt: cu.skippedAt
        ? firestoreToDateTime(cu.skippedAt)?.toLocaleString()
        : undefined,
      cancelledAt: cu.cancelledAt
        ? firestoreToDateTime(cu.cancelledAt)?.toLocaleString()
        : undefined,
      userId: getEmployeeName(cu.userId, serviceAdvisors, true),
      createdBy: getEmployeeName(cu.createdBy, serviceAdvisors, true),
      updatedBy: getEmployeeName(cu.updatedBy, serviceAdvisors, true),
      scheduledBy: getEmployeeName(cu.scheduledBy, serviceAdvisors, true),
      pendingBy: getEmployeeName(cu.pendingBy, serviceAdvisors, true),
      skippedBy: getEmployeeName(cu.skippedBy, serviceAdvisors, true),
      cancelledBy: getEmployeeName(cu.cancelledBy, serviceAdvisors, true),
      completedAt:
        cu.status === CustomerUpdateStatus.CANCELLED
          ? firestoreToDateTime(cu.cancelledAt)?.toLocaleString()
          : cu.status === CustomerUpdateStatus.SKIPPED
            ? firestoreToDateTime(cu.skippedAt)?.toLocaleString()
            : firestoreToDateTime(cu.completedAt)?.toLocaleString(),
      completedBy:
        cu.status === CustomerUpdateStatus.CANCELLED
          ? getEmployeeName(cu.cancelledBy, serviceAdvisors, true)
          : cu.status === CustomerUpdateStatus.SKIPPED
            ? getEmployeeName(cu.skippedBy, serviceAdvisors, true)
            : getEmployeeName(cu.completedBy, serviceAdvisors, true),
      completedInContainer: cu.completedInContainer
        ? getContainerName(cu.completedInContainer, containers)
        : undefined,
    }));
  };

  // Helper function to get sorting from params
  const getSortingFromParams = (): SortingState => {
    if (sortBy && sortOrder) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    }
    return [];
  };

  // Initialize state from URL params
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(getSortingFromParams());

  const [filtering, setFiltering] = useState<{
    [key: string]: string | undefined;
  }>({
    status: status || undefined,
    userId: userId || undefined,
    cardNumber: cardNumberFilter || undefined,
  });

  // Update pagination state when URL changes (browser back/forward)
  useEffect(() => {
    const newPageIndex = currentPage - 1;
    const newPageSize = pageSize;

    // Only update if values actually changed
    if (
      pagination.pageIndex !== newPageIndex ||
      pagination.pageSize !== newPageSize
    ) {
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      });
    }
  }, [currentPage, pageSize]); // Remove pagination from dependencies

  // Update sorting state when URL changes
  useEffect(() => {
    const newSorting = getSortingFromParams();

    // Only update if sorting actually changed
    const sortingChanged =
      sorting.length !== newSorting.length ||
      (newSorting.length > 0 &&
        (sorting.length === 0 ||
          sorting[0].id !== newSorting[0].id ||
          sorting[0].desc !== newSorting[0].desc));

    if (sortingChanged) {
      setSorting(newSorting);
    }
  }, [sortBy, sortOrder]); // Use specific params instead of searchParams

  // Update filtering state when URL changes
  useEffect(() => {
    const newStatus = status as CustomerUpdateStatus | undefined;
    const newUserId = userId || undefined;
    const newCardNumber = cardNumberFilter || undefined;
    const newFiltering = {
      status: newStatus,
      userId: newUserId,
      cardNumber: newCardNumber,
    };
    // Only update if filtering actually changed
    const filteringChanged =
      filtering.status !== newFiltering.status ||
      filtering.userId !== newFiltering.userId ||
      filtering.cardNumber !== newFiltering.cardNumber;

    if (filteringChanged) {
      setFiltering(newFiltering);
    }
  }, [status, userId, cardNumberFilter]); // Use specific params instead of searchParams

  // Update URL when pagination changes and save to localStorage automatically
  useEffect(() => {
    const urlPage = currentPage;
    const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const actualPage = pagination.pageIndex + 1;

    // Only update URL if pagination state differs from URL state
    if (actualPage !== urlPage || pagination.pageSize !== urlPageSize) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", actualPage.toString());
      newParams.set("pageSize", pagination.pageSize.toString());
      setSearchParams(newParams);
    }

    // Save pagination state to localStorage automatically
    saveTableState({
      sorting: sorting,
      filtering: filtering,
      pagination: pagination,
    });
  }, [pagination.pageIndex, pagination.pageSize]); // Remove searchParams from dependencies

  // Handle sorting changes and reset pagination
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);

    // Save sorting state to localStorage
    saveTableState({
      sorting: newSorting,
      filtering: filtering,
      pagination: pagination,
    });

    // Update URL with new sorting and reset to page 1
    const newParams = new URLSearchParams(searchParams);

    if (newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    } else {
      newParams.delete("sortBy");
      newParams.delete("sortOrder");
    }

    // Reset to page 1 when sorting changes
    newParams.set("page", "1");
    setSearchParams(newParams);

    // Update local pagination state immediately to prevent flicker
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Handle filtering changes and reset pagination
  const handleFilteringChange = (
    updaterOrValue:
      | { [key: string]: string | undefined }
      | ((old: { [key: string]: string | undefined }) => {
          [key: string]: string | undefined;
        })
  ) => {
    const newFiltering =
      typeof updaterOrValue === "function"
        ? updaterOrValue(filtering)
        : updaterOrValue;

    setFiltering(newFiltering);

    // Check if status has changed to apply default sorting
    const statusChanged = filtering.status !== newFiltering.status;
    let newSorting = sorting;

    if (statusChanged) {
      // Apply default sorting for the new status
      newSorting = getDefaultSortingForStatus(
        newFiltering.status as CustomerUpdateStatus | "all"
      );
      setSorting(newSorting);
    }

    // Save filtering and sorting state to localStorage
    saveTableState({
      sorting: newSorting,
      filtering: newFiltering,
      pagination: pagination,
    });

    // Update URL with new filtering and reset to page 1
    const newParams = new URLSearchParams(searchParams);

    if (newFiltering.status) {
      newParams.set("status", newFiltering.status);
    } else {
      newParams.delete("status");
    }

    if (newFiltering.userId) {
      newParams.set("userId", newFiltering.userId);
    } else {
      newParams.delete("userId");
    }

    if (newFiltering.cardNumber) {
      newParams.set("cardNumber", newFiltering.cardNumber);
    } else {
      newParams.delete("cardNumber");
    }

    // Update sorting in URL if status changed
    if (statusChanged && newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    }

    // Reset to page 1 when filtering changes
    newParams.set("page", "1");
    setSearchParams(newParams);

    // Update local pagination state immediately to prevent flicker
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const CardNumberFilter = () => {
    const [inputValue, setInputValue] = useReactState(cardNumberFilter || "");

    const handleApplyFilter = () => {
      handleFilteringChange({
        ...filtering,
        cardNumber: inputValue.trim() || undefined,
      });
    };

    const handleClearFilter = () => {
      setInputValue("");
      handleFilteringChange({
        ...filtering,
        cardNumber: undefined,
      });
    };

    return (
      <div className="flex flex-row items-end">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-200">
            Search RO Number
          </label>
          <div className="flex flex-row gap-0">
            <Input
              placeholder="Enter RO number..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleApplyFilter();
                }
              }}
              className="w-48 rounded-r-none"
            />
            <button
              onClick={handleApplyFilter}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-r-md border border-l-0 border-muted bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
                cardNumberFilter && "rounded-r-none"
              )}
              disabled={inputValue.trim() === (cardNumberFilter || "")}
            >
              <Icon name={IconNames.search} />
            </button>
            {cardNumberFilter && (
              <button
                onClick={handleClearFilter}
                className="flex h-10 w-10 items-center justify-center rounded-r-md border border-l-0 border-muted bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name={IconNames.clear} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const StatusFilter = () => {
    const handleFilterButtonClick = (status: CustomerUpdateStatus | "all") => {
      handleFilteringChange({
        ...filtering,
        status: filtering.status === status ? undefined : status,
      });
    };

    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-200">
          Filter by Status
        </label>
        <div className="flex w-48 flex-row flex-wrap gap-2">
          <Select
            onValueChange={(value) =>
              handleFilterButtonClick(value as CustomerUpdateStatus | "all")
            }
            value={filtering.status || "all"}
            options={[
              { label: "All", value: "all" },
              ...[
                "PENDING",
                "CREATED",
                "SCHEDULED",
                "SNOOZED",
                "COMPLETED",
                "CANCELLED",
                "SKIPPED",
              ].map((key) => {
                const value =
                  CustomerUpdateStatus[
                    key as keyof typeof CustomerUpdateStatus
                  ];
                return { label: CustomerUpdateStatusLabels[value], value };
              }),
            ]}
          />
        </div>
      </div>
    );
  };

  const MyUpdatesFilter = () => {
    const isMyUpdatesActive = filtering.userId === authUser?.uid;

    const handleToggleMyUpdates = (checked: boolean) => {
      handleFilteringChange({
        ...filtering,
        userId: checked ? authUser?.uid : undefined,
      });
    };

    return (
      <div className="flex flex-col justify-end gap-1">
        <div className="flex h-10 flex-col justify-center">
          <CheckboxInput
            className="h-6 w-6"
            label="Show only my updates"
            checked={isMyUpdatesActive}
            onCheckedChange={handleToggleMyUpdates}
            disabled={!authUser?.uid}
          />
        </div>
      </div>
    );
  };

  const ResetFiltersButton = () => {
    const hasActiveFilters =
      filtering.status !== CustomerUpdateStatus.PENDING ||
      filtering.userId !== undefined ||
      filtering.cardNumber !== undefined ||
      sorting.length === 0 ||
      sorting[0]?.id !== "createdAt" ||
      !sorting[0]?.desc;

    const handleReset = () => {
      const defaultSorting: SortingState = [{ id: "createdAt", desc: true }];
      const defaultFiltering = {
        status: CustomerUpdateStatus.PENDING,
        userId: undefined,
        cardNumber: undefined,
      };

      // Reset local state
      setSorting(defaultSorting);
      setFiltering(defaultFiltering);

      // Reset saved state in localStorage
      resetTableState();

      // Reset URL parameters
      const newParams = new URLSearchParams(searchParams);
      newParams.set("sortBy", "createdAt");
      newParams.set("sortOrder", "desc");
      newParams.set("status", CustomerUpdateStatus.PENDING);
      newParams.delete("userId");
      newParams.delete("cardNumber");
      newParams.set("page", "1");
      setSearchParams(newParams);

      // Update local pagination state
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };

    if (!hasActiveFilters) {
      return null; // Don't render anything if no active filters
    }

    return (
      <div className="flex flex-col justify-end gap-1">
        <div className="flex h-10 flex-col justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleReset}
                  variant="ghost-alt"
                  disabled={!hasActiveFilters}
                  className="px-3"
                >
                  <Icon name={IconNames.clearFilters} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Reset all filters and sorting to defaults
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    );
  };

  const actions = (customerUpdate: DatatableCustomerUpdate) => {
    return [
      // View button
      <TooltipProvider key={"view"}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate(`${customerUpdate.id}`)}
              variant="ghost-alt"
              id="view_customer_update"
            >
              <Icon name={IconNames.open} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            View this customer update&#39;s details.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>,
    ];
  };

  return (
    <DataTable
      // Data is based on the data list, with pagination applied
      data={
        data?.docs.map((cu) => ({
          ...cu,
          comments:
            cu.status === CustomerUpdateStatus.CANCELLED
              ? cu.cancelledReason
              : cu.status === CustomerUpdateStatus.SKIPPED
                ? cu.skippedReason
                : cu.comments,
          completedAt:
            cu.status === CustomerUpdateStatus.CANCELLED
              ? cu.cancelledAt
              : cu.status === CustomerUpdateStatus.SKIPPED
                ? cu.skippedAt
                : cu.completedAt,
          completedBy:
            cu.status === CustomerUpdateStatus.CANCELLED
              ? cu.cancelledBy
              : cu.status === CustomerUpdateStatus.SKIPPED
                ? cu.skippedBy
                : cu.completedBy,
          completedInContainer: cu.completedInContainer
            ? getContainerName(cu.completedInContainer, containers)
            : undefined,
        })) ?? []
      }
      columns={customerUpdateColumns({
        actions: actions,
        employees: serviceAdvisors,
        currentStatus: filtering.status as
          | CustomerUpdateStatus
          | "all"
          | undefined,
      })}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={handleSortingChange}
      rowCount={data?.total}
      loading={!data || isLoading}
      showRecordRange={true}
      onExportData={handleExportData}
      exportFilename="customer-updates.csv"
      userDefaults={defaultState}
      factoryDefaults={layoutDefaults}
      onLayoutSave={saveTableLayout}
      onLayoutReset={resetTableLayout}
      headingContent={
        <div className={"flex flex-row gap-2 p-2"}>
          <StatusFilter />
          <div className={"w-6"}></div>
          <CardNumberFilter />
          <div className={"w-6"}></div>
          <MyUpdatesFilter />
          <div className={"w-6"}></div>
          <ResetFiltersButton />
        </div>
      }
    />
  );
};
